import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const scrollPositions = new Map<string, number>();

const restoreScrollPosition = (position: number) => {
  let attempts = 0;

  const restore = () => {
    window.scrollTo(0, position);
    attempts += 1;

    if (attempts < 60 && Math.abs(window.scrollY - position) > 2) {
      window.requestAnimationFrame(restore);
    }
  };

  window.requestAnimationFrame(restore);
};

const ScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);
  const pathnameRef = useRef<string | null>(null);

  useEffect(() => {
    keyRef.current = location.key;
  }, [location.key]);

  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useEffect(() => {
    const saveScrollPosition = () => {
      scrollPositions.set(location.key, window.scrollY);
    };

    window.addEventListener("scroll", saveScrollPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", saveScrollPosition);
    };
  }, [location.key]);

  useEffect(() => {
    const savedPosition = scrollPositions.get(location.key);
    const isPathnameChange = pathnameRef.current == null || pathnameRef.current !== location.pathname;

    // ページ分割一覧は必要なページの取得完了後に自分で位置を復元する。
    if (document.querySelector('[data-scroll-restoration="managed"]')) {
      pathnameRef.current = location.pathname;
      return;
    }

    if (navigationType === "POP" && savedPosition != null) {
      restoreScrollPosition(savedPosition);
    } else if (isPathnameChange) {
      window.scrollTo(0, 0);
    }

    pathnameRef.current = location.pathname;
  }, [location.key, location.pathname, navigationType]);

  useEffect(() => {
    const saveCurrentPosition = () => {
      scrollPositions.set(keyRef.current, window.scrollY);
    };

    window.addEventListener("pagehide", saveCurrentPosition);

    return () => {
      window.removeEventListener("pagehide", saveCurrentPosition);
    };
  }, []);

  return null;
};

export default ScrollRestoration;
